/**
 * Network Collector
 * Intercepts and tracks fetch requests
 */

import { BaseCollector } from './BaseCollector.js';
import type { EventBus } from '../core/EventBus.js';
import type { BreadcrumbManager } from '../utils/session.js';
import { createErrorEvent } from '../models/ErrorEvent.js';
import { SeverityClassifier } from '../strategies/severity/index.js';

export class NetworkCollector extends BaseCollector {
  private breadcrumbManager: BreadcrumbManager;
  private severityClassifier: SeverityClassifier;
  private originalFetch: typeof window.fetch | null = null;
  private endpoint: string;
  private sessionRecordingEndpoint?: string;

  constructor(
    eventBus: EventBus,
    sessionId: string,
    userId: string | null,
    breadcrumbManager: BreadcrumbManager,
    config: {
      endpoint: string;
      sessionRecordingEndpoint?: string;
      debug: boolean;
    }
  ) {
    super('NetworkCollector', eventBus, sessionId, userId, config.debug);
    this.breadcrumbManager = breadcrumbManager;
    this.severityClassifier = new SeverityClassifier();
    this.endpoint = config.endpoint;
    this.sessionRecordingEndpoint = config.sessionRecordingEndpoint;
  }

  initialize(): void {
    this.log('Initializing network request tracking');

    // Store original fetch with proper context binding
    this.originalFetch = window.fetch.bind(window);

    // Wrap fetch
    window.fetch = async (...args): Promise<Response> => {
      const startTime = Date.now();
      const url = typeof args[0] === 'string' ? args[0] : args[0].toString();
      const method = (args[1]?.method || 'GET').toUpperCase();
      const referrer = document.referrer || window.location.href;

      // Don't track watchdog's own API calls to prevent loops
      if (
        url === this.endpoint ||
        url === this.sessionRecordingEndpoint ||
        url.includes('/watchdog')
      ) {
        return this.originalFetch!(...args);
      }

      // Add breadcrumb
      try {
        this.breadcrumbManager.add('xhr', `${method} ${url}`);
      } catch (error) {
        // Ignore breadcrumb errors
      }

      try {
        const response = await this.originalFetch!(...args);
        const duration = Date.now() - startTime;

        // Get response size
        const clonedResponse = response.clone();
        let responseSize = 0;

        try {
          const contentLength = response.headers.get('content-length');
          if (contentLength) {
            responseSize = parseInt(contentLength, 10);
          } else {
            const blob = await clonedResponse.blob();
            responseSize = blob.size;
          }
        } catch (e) {
          // Can't read size, continue
        }

        // Collect API call event
        const baseMetadata = this.getBaseMetadata();
        const apiCallEvent = {
          ...baseMetadata,
          event_category: 'behavior' as const,
          event_type: 'api_call',
          api_url: url,
          api_method: method,
          api_status: response.status,
          api_status_text: response.statusText,
          api_duration: duration,
          api_response_size: responseSize,
          api_referrer: referrer,
          api_success: response.ok,
          api_content_type: response.headers.get('content-type') || null
        };

        this.emit('event:collected', apiCallEvent);

        // Track errors if not successful
        if (!response.ok) {
          const severity = this.severityClassifier.classify({
            type: 'network',
            error: `HTTP ${response.status}: ${response.statusText}`,
            statusCode: response.status,
            url
          });

          const errorEvent = createErrorEvent(
            baseMetadata,
            `HTTP ${response.status}: ${response.statusText}`,
            'network_error',
            severity,
            this.breadcrumbManager.getAll(),
            {
              httpStatus: response.status,
              httpStatusText: response.statusText,
              resourceUrl: url
            }
          );

          this.emit('event:collected', errorEvent);
        }

        return response;
      } catch (error) {
        const duration = Date.now() - startTime;

        // Collect failed API call
        const baseMetadata = this.getBaseMetadata();
        const apiCallEvent = {
          ...baseMetadata,
          event_category: 'behavior' as const,
          event_type: 'api_call',
          api_url: url,
          api_method: method,
          api_status: 0,
          api_status_text: 'Network Error',
          api_duration: duration,
          api_response_size: 0,
          api_referrer: referrer,
          api_success: false,
          api_error: (error as Error).message
        };

        this.emit('event:collected', apiCallEvent);

        // Track network error
        const severity = this.severityClassifier.classify({
          type: 'network',
          error: error as Error,
          url
        });

        const errorEvent = createErrorEvent(
          baseMetadata,
          `Network error: ${url}`,
          'network_error',
          severity,
          this.breadcrumbManager.getAll()
        );

        this.emit('event:collected', errorEvent);

        // Emit critical error if needed
        if (severity === 'critical' || severity === 'high') {
          this.emit('error:critical', errorEvent);
        }

        throw error;
      }
    };

    this.isInitialized = true;
  }

  destroy(): void {
    // Restore original fetch
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }

    this.isInitialized = false;
    this.log('Destroyed');
  }
}

